// Geometry for keeping an open dropdown menu inside the screen. Kept free of React
// and styled-components so the maths can be read (and reasoned about) on its own.

// Smallest gap we keep between an open menu and the edge of the screen.
export const MENU_VIEWPORT_PADDING = 8

// A menu's height is read from getBoundingClientRect while it fits and from
// scrollHeight once it scrolls, and browsers disagree about rounding and about the
// bottom padding. A menu sitting on the limit must not toggle its scrollbar on and
// off forever, so it only stops scrolling once its content is clearly smaller.
const FIT_HYSTERESIS = 24

// Correction applied on top of the menu's natural position. A menu that already fits
// gets NO_FIT, so nothing moves from where it has always opened.
export interface MenuFit {
  dx: number
  dy: number
  maxHeight: number | null
}

export const NO_FIT: MenuFit = { dx: 0, dy: 0, maxHeight: null }

interface Box {
  left: number
  top: number
  bottom: number
  width: number
}

interface MenuFitInput {
  // Where the menu wants to sit, with any correction already taken back out.
  menu: Box
  trigger: Box
  // Full height of the menu, including anything a previous fit scrolled away.
  contentHeight: number
  appliedMaxHeight: number | null
  viewportWidth: number
  viewportHeight: number
}

export const sameFit = (a: MenuFit, b: MenuFit) =>
  a.dx === b.dx && a.dy === b.dy && a.maxHeight === b.maxHeight

export const computeMenuFit = ({
  menu,
  trigger,
  contentHeight,
  appliedMaxHeight,
  viewportWidth,
  viewportHeight,
}: MenuFitInput): MenuFit => {
  const pad = MENU_VIEWPORT_PADDING

  let dx = 0
  if (menu.left + menu.width > viewportWidth - pad) {
    dx = viewportWidth - pad - (menu.left + menu.width)
  }
  if (menu.left + dx < pad) dx = pad - menu.left

  let dy = 0
  let maxHeight: number | null = null
  if (menu.top + contentHeight > viewportHeight - pad) {
    // Flip above the trigger, keeping the gap the menu normally opens with.
    const gap = Math.max(0, menu.top - trigger.bottom)
    const flippedTop = trigger.top - gap - contentHeight
    if (flippedTop >= pad) {
      dy = flippedTop - menu.top
    } else {
      // No room on either side of the trigger. Fill the screen and scroll inside the
      // menu when it is taller than the screen, otherwise slide it up against the
      // bottom edge so it stays as close to its button as it can.
      const roomForMenu = viewportHeight - pad * 2
      const keepsScrolling =
        appliedMaxHeight !== null &&
        contentHeight > roomForMenu - FIT_HYSTERESIS

      if (contentHeight > roomForMenu || keepsScrolling) {
        maxHeight = roomForMenu
        dy = pad - menu.top
      } else {
        dy = viewportHeight - pad - contentHeight - menu.top
      }
    }
  }
  if (menu.top + dy < pad) dy = pad - menu.top

  return { dx, dy, maxHeight }
}
